import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import {
    makeSleKey,
    makeSleChildKey,
    normalizeSleRatingForUi,
    normalizeSleRatingForApi,
    parseSleUiKey
} from "../utils/teacher/SleBatchUtils";

/**
 * Batch SLE ratings for HOMEROOM view.
 */
const useHomeroomSleRatings = ({ enabled, profileLocationKey, selectedEpr, token, userGuid }) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [schoolLevelExpectations, setSchoolLevelExpectations] = useState([]);
    const [sleCommentsByStudent, setSleCommentsByStudent] = useState({});
    const [valuesByStudent, setValuesByStudent] = useState({}); // { [gpsId]: { [uiKey]: rating } }
    // Join overlapping saves so two PUTs with key: null cannot CREATE duplicate SLE comments.
    const saveInFlightRef = useRef(null);

    const gradingProfileId = selectedEpr?.key;

    const childToParentMap = useMemo(() => {
        const map = {};
        for (const sle of schoolLevelExpectations || []) {
            for (const child of sle.sleChildList || []) {
                map[child.key] = sle.key;
            }
        }
        return map;
    }, [schoolLevelExpectations]);

    const hydrateRatingsFromPayload = useCallback((payload) => {
        const nextValues = {};

        for (const row of payload || []) {
            const gpsId = Number(row.key || row.gradingProfileStudentId);
            const map = {};

            for (const r of row.sleRatings || []) {
                const uiKey = r.sleChildId == null ? makeSleKey(r.sleId) : makeSleChildKey(r.sleChildId);
                map[uiKey] = normalizeSleRatingForUi(r.rating);
            }

            nextValues[gpsId] = map;
        }

        setValuesByStudent(nextValues);
    }, []);

    const hydrateCommentsFromPayload = useCallback((payload) => {
        const nextComments = {};

        for (const row of payload || []) {
            const gpsId = Number(row.key || row.gradingProfileStudentId);

            if (row.studentSleComment) {
                nextComments[gpsId] = {
                    key: row.studentSleComment.key != null ? Number(row.studentSleComment.key) : null,
                    comment: row.studentSleComment.comment || ""
                };
            }
        }

        setSleCommentsByStudent(nextComments);
    }, []);

    // Fetch SLE list for location/year
    useEffect(() => {
        if (!enabled || !selectedEpr || !profileLocationKey) return;
        const { schoolYearKey } = selectedEpr;
        setLoading(true);

        ElementaryProgressReportDao({
            action: "getSchoolLevelExpectations",
            schoolYearKey,
            locationKey: profileLocationKey,
            token
        })
            .then((res) => {
                if (res?.data?.payload) setSchoolLevelExpectations(res.data.payload);
            })
            .catch((error) => {
                console.error("Failed to load SLE list", error);
                toast.error("There was a problem loading School Level Expectations. Please try again later.");
            })
            .finally(() => setLoading(false));
    }, [enabled, profileLocationKey, selectedEpr, token]);

    // Fetch homeroom SLE ratings once SLE is enabled
    useEffect(() => {
        if (!enabled || !gradingProfileId || !token) return;

        setLoading(true);
        const options = {
            action: "getSleRatingsHomeroom",
            gradingProfileId,
            token
        };

        ElementaryProgressReportDao(options)
            .then((response) => {
                const payload = response?.data?.payload || [];
                setRows(payload);

                hydrateRatingsFromPayload(payload);
                hydrateCommentsFromPayload(payload);
            })
            .catch((error) => {
                console.error("Failed to load homeroom SLE ratings", error);
                toast.error("There was a problem loading School Level Expectation ratings. Please try again later.");
            })
            .finally(() => setLoading(false));
    }, [enabled, gradingProfileId, hydrateCommentsFromPayload, hydrateRatingsFromPayload, token]);

    const onChangeSleRating = useCallback((gradingProfileStudentId, uiKey, value) => {
        setValuesByStudent((prev) => {
            const gpsId = Number(gradingProfileStudentId);
            const prevMap = prev[gpsId] || {};
            return {
                ...prev,
                [gpsId]: {
                    ...prevMap,
                    [uiKey]: value || null // null is a delete
                }
            };
        });
        setHasChanges(true);
    }, []);

    const onChangeSleComment = useCallback((gradingProfileStudentId, comment) => {
        const gpsId = Number(gradingProfileStudentId);
        setSleCommentsByStudent((prev) => ({
            ...prev,
            [gpsId]: {
                ...(prev[gpsId] || { key: null }),
                comment
            }
        }));
        setHasChanges(true);
    }, []);

    const findExistingRatingKey = useCallback(
        (gradingProfileStudentId, sleId, sleChildId) => {
            const gpsId = Number(gradingProfileStudentId);
            const row = rows.find((r) => Number(r.key || r.gradingProfileStudentId) === gpsId);
            if (!row) return null;
            const match = (row.sleRatings || []).find(
                (r) =>
                    Number(r.sleId) === Number(sleId) &&
                    ((sleChildId == null && (r.sleChildId == null || r.sleChildId === 0)) ||
                        Number(r.sleChildId) === Number(sleChildId))
            );
            return match ? Number(match.key) || null : null;
        },
        [rows]
    );

    const buildRatingsPayload = useCallback(() => {
        if (!selectedEpr) return [];

        const result = [];
        const students = selectedEpr.gradingProfileStudents || [];

        for (const student of students) {
            const gpsId = Number(student.key);

            const baseRow = rows.find((r) => Number(r.key || r.gradingProfileStudentId) === gpsId) || {};

            const valueMap = valuesByStudent[gpsId] || {};
            const existingRatings = baseRow.sleRatings || [];

            const allUiKeys = new Set([
                ...Object.keys(valueMap),
                ...existingRatings.map((r) =>
                    r.sleChildId == null ? makeSleKey(r.sleId) : makeSleChildKey(r.sleChildId)
                )
            ]);

            for (const uiKey of allUiKeys) {
                const { sleId, sleChildId } = parseSleUiKey(uiKey);

                if (!sleId && !sleChildId) continue;

                let uiVal;
                if (Object.prototype.hasOwnProperty.call(valueMap, uiKey)) {
                    uiVal = valueMap[uiKey];
                } else {
                    const match = existingRatings.find((r) =>
                        r.sleChildId == null ? uiKey === makeSleKey(r.sleId) : uiKey === makeSleChildKey(r.sleChildId)
                    );
                    uiVal = match ? normalizeSleRatingForUi(match.rating) : undefined;
                }

                if (uiVal === undefined) continue;

                // Figure out the correct parent sleId
                let finalSleId = sleId;
                if (!finalSleId && sleChildId) {
                    finalSleId = childToParentMap[sleChildId];
                }
                if (!finalSleId) continue;

                const existingKey = findExistingRatingKey(gpsId, finalSleId, sleChildId || null);

                result.push({
                    key: existingKey,
                    sleId: Number(finalSleId),
                    sleChildId: sleChildId ? Number(sleChildId) : null,
                    gradingProfileStudentId: gpsId,
                    rating: normalizeSleRatingForApi(uiVal),
                    personalizedLearningPlan: false,
                    lastRaterGuid: userGuid
                });
            }
        }

        return result;
    }, [rows, selectedEpr, valuesByStudent, childToParentMap, findExistingRatingKey, userGuid]);

    const saveHomeroomSleComments = useCallback(async () => {
        const entries = Object.entries(sleCommentsByStudent || {});
        if (!entries.length) return;

        const rows = entries.map(([gpsId, { key, comment }]) => {
            const numericGpsId = Number(gpsId);

            // comment deletes:
            // if existing key AND comment is "" then send null (delete)
            const normalizedComment = key != null && (comment === "" || comment == null) ? null : (comment ?? "");

            return {
                key: key != null ? String(key) : null,
                gradingProfileStudentId: numericGpsId,
                comment: normalizedComment,
                lastUpdaterGuid: userGuid
            };
        });

        const options = {
            action: "updateSleRatingCommentsHomeroom",
            gradingProfileId,
            data: rows,
            token
        };

        try {
            const res = await ElementaryProgressReportDao(options);
            const payload = res?.data?.payload || [];

            // Normalize returned array into sleCommentsByStudent
            const map = {};
            for (const row of payload) {
                const gpsId = Number(row.gradingProfileStudentId);
                map[gpsId] = {
                    key: row.key != null ? Number(row.key) : null,
                    comment: row.comment ?? ""
                };
            }
            setSleCommentsByStudent(map);

            setHasChanges(false);
        } catch (e) {
            console.error("Failed to save homeroom SLE comments", e);
            toast.error("Comments were not saved. Please make note of your comments and try again later.");
        }
    }, [gradingProfileId, sleCommentsByStudent, token, userGuid]);

    const saveHomeroomSleRatings = useCallback(async () => {
        if (!enabled || !gradingProfileId || !token) return;

        if (saveInFlightRef.current) {
            return saveInFlightRef.current;
        }

        const run = (async () => {
            const data = buildRatingsPayload();

            const ratingOptions = {
                action: "updateSleRatingsHomeroom",
                gradingProfileId,
                data,
                token
            };

            try {
                setLoading(true);
                // save both ratings and comments in parallel
                const [ratingsRes] = await Promise.all([
                    ElementaryProgressReportDao(ratingOptions),
                    saveHomeroomSleComments()
                ]);

                // refresh rows from SLE rating save
                const payload = ratingsRes?.data?.payload || [];
                setRows(payload);
                hydrateRatingsFromPayload(payload);

                setHasChanges(false);
                toast.success("School Level Expectation ratings and comments successfully saved.");
            } catch (e) {
                console.error("Failed to save homeroom SLE ratings/comments", e);
                toast.error("There was a problem saving School Level Expectation ratings. Please try again later.");
            } finally {
                setLoading(false);
            }
        })();

        saveInFlightRef.current = run;
        try {
            return await run;
        } finally {
            saveInFlightRef.current = null;
        }
    }, [buildRatingsPayload, enabled, gradingProfileId, hydrateRatingsFromPayload, token, saveHomeroomSleComments]);

    const gradingProfileStudentIds = useMemo(() => rows.map((r) => Number(r.key || r.gradingProfileStudentId)), [rows]);

    return {
        loading,
        gradingProfileStudentIds,
        valuesByStudent,
        schoolLevelExpectations,
        sleCommentsByStudent,
        onChangeSleRating,
        onChangeSleComment,
        saveHomeroomSleRatings,
        hasChanges
    };
};

export default useHomeroomSleRatings;
